import { useState, useRef, useCallback } from 'react';

const THRESHOLD = 60;
const MAX_PULL = 100;
const RESISTANCE = 0.4;

/**
 * Pull-to-refresh wrapper component.
 * Only engages when the inner content is scrolled to the top.
 * Calls `onRefresh()` (async) when pull threshold is met.
 */
export default function PullToRefresh({ onRefresh, children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const containerRef = useRef(null);
  const pullingRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (refreshing) return;
    // Only engage if scrolled to top
    const el = containerRef.current;
    if (el && el.scrollTop <= 0) {
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = false;
    } else {
      startYRef.current = null;
    }
  }, [refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (startYRef.current === null || refreshing) return;
    const delta = (e.touches[0].clientY - startYRef.current) * RESISTANCE;
    if (delta > 0) {
      pullingRef.current = true;
      setPullDistance(Math.min(delta, MAX_PULL));
      // Prevent native scroll while pulling
      if (containerRef.current && containerRef.current.scrollTop <= 0) {
        e.preventDefault();
      }
    } else {
      pullingRef.current = false;
      setPullDistance(0);
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (startYRef.current === null || refreshing) return;
    startYRef.current = null;

    if (pullDistance >= THRESHOLD && onRefresh) {
      setRefreshing(true);
      setPullDistance(THRESHOLD); // Hold at threshold position while refreshing
      try {
        await onRefresh();
      } catch {
        // Swallow errors
      }
      setRefreshing(false);
    }
    setPullDistance(0);
    pullingRef.current = false;
  }, [pullDistance, onRefresh, refreshing]);

  const rotation = Math.min((pullDistance / THRESHOLD) * 180, 180);
  const showIndicator = pullDistance > 5 || refreshing;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        touchAction: pullingRef.current ? 'none' : 'auto',
      }}
    >
      {/* Pull indicator */}
      {showIndicator && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: pullDistance > 0 ? pullDistance : THRESHOLD,
            overflow: 'hidden',
            transition: refreshing ? 'none' : pullDistance === 0 ? 'height 0.25s ease' : 'none',
          }}
        >
          {refreshing ? (
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '2.5px solid var(--border)',
                borderTopColor: 'var(--accent)',
                animation: 'ptr-spin 0.8s linear infinite',
              }}
            />
          ) : (
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.1s ease',
                opacity: Math.min(pullDistance / (THRESHOLD * 0.5), 1),
              }}
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <polyline points="19 12 12 19 5 12" />
            </svg>
          )}
        </div>
      )}
      {children}
      <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
